import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { collection, doc } from 'firebase/firestore'
import { getDocWithError } from '../firestore-mobx/fetch'
import { fetchPublicUser } from '../firestore/PublicUser'
import { fetchUserProfile } from '../firestore/UserProfile'
import { fetchSectionsForUser } from '../firestore/Section'
import { fetchRoomStatesForUser } from '../firestore/RoomState'
import { fetchPaymentsForUser } from '../firestore/Payment'
import { fetchSectionPassesAndRebatesForUser } from '../firestore/UserProfileSectionPass'
import { fetchUserProfileTokens } from '../firestore/UserProfileToken'
import { fetchUserProfilePurchases } from '../firestore/UserProfilePurchase'
import type { PublicUser } from '../models/PublicUser'
import type { UserProfile } from '../models/UserProfile'
import type { Section } from '../models/Section'
import type { RoomState } from '../models/RoomState'
import type { UserProfileSectionPass } from '../models/UserProfileSectionPass'
import type { UserProfileSectionPassRebate } from '../models/UserProfileSectionPassRebate'
import type { UserProfileToken } from '../models/UserProfileToken'
import type { UserProfilePurchase } from '../models/UserProfilePurchase'
import type { FirestorePayment } from '../firestore/Payment/schema'

// Payment type with id for UI consumption
export type PaymentWithId = FirestorePayment & { id: string }

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error'

export class AdminUserInspectorCubit extends Cubit {
  repository: FirebaseRepository
  userId: string

  @observable
  loadingState: LoadingState = 'loading'

  @observable
  error: string | null = null

  @observable
  user: PublicUser | null = null

  @observable
  profile: UserProfile | null = null

  @observable
  sections: Section[] = []

  @observable
  rooms: RoomState[] = []

  @observable
  payments: PaymentWithId[] = []

  @observable
  sectionPasses: UserProfileSectionPass[] = []

  @observable
  sectionPassRebates: UserProfileSectionPassRebate[] = []

  @observable
  tokens: UserProfileToken[] = []

  @observable
  purchases: UserProfilePurchase[] = []

  @observable
  slideDeckNames: Map<string, string> = new Map()

  constructor(repository: FirebaseRepository, userId: string) {
    super()
    makeObservable(this)
    this.userId = userId
    this.repository = repository
  }

  initialize(): void {
    this.fetchAllData()
  }

  @action
  async fetchAllData() {
    this.loadingState = 'loading'
    this.error = null

    // Fetch all data in parallel using Promise.allSettled
    const results = await Promise.allSettled([
      fetchPublicUser(this.repository, { userId: this.userId }),
      fetchUserProfile(this.repository, { userId: this.userId }),
      fetchSectionsForUser(this.repository, { userId: this.userId }),
      fetchRoomStatesForUser(this.repository, { userId: this.userId }),
      fetchPaymentsForUser(this.repository.firestore, { userId: this.userId }),
      fetchSectionPassesAndRebatesForUser(this.repository, {
        userId: this.userId,
      }),
      fetchUserProfileTokens(this.repository, { userId: this.userId }),
      fetchUserProfilePurchases(this.repository, { userId: this.userId }),
    ])

    // Process results, logging warnings for any failures
    const [
      userResult,
      profileResult,
      sectionsResult,
      roomsResult,
      paymentsResult,
      sectionPassesResult,
      tokensResult,
      purchasesResult,
    ] = results

    runInAction(() => {
      // Track if critical fetches failed (user and profile are required)
      let hasCriticalError = false

      if (userResult.status === 'fulfilled') {
        this.user = userResult.value
        if (!userResult.value) {
          hasCriticalError = true
          this.error = 'User not found'
        }
      } else {
        hasCriticalError = true
        this.error = 'Failed to fetch user'
        console.warn('Failed to fetch user:', userResult.reason)
      }

      if (profileResult.status === 'fulfilled') {
        this.profile = profileResult.value
      } else {
        hasCriticalError = true
        this.error = this.error || 'Failed to fetch user profile'
        console.warn('Failed to fetch user profile:', profileResult.reason)
      }

      if (sectionsResult.status === 'fulfilled') {
        this.sections = sectionsResult.value
      } else {
        console.warn('Failed to fetch sections:', sectionsResult.reason)
      }

      if (roomsResult.status === 'fulfilled') {
        this.rooms = roomsResult.value
      } else {
        console.warn('Failed to fetch rooms:', roomsResult.reason)
      }

      if (paymentsResult.status === 'fulfilled') {
        this.payments = paymentsResult.value
      } else {
        console.warn('Failed to fetch payments:', paymentsResult.reason)
      }

      if (sectionPassesResult.status === 'fulfilled') {
        this.sectionPasses = sectionPassesResult.value.passes
        this.sectionPassRebates = sectionPassesResult.value.rebates
      } else {
        console.warn(
          'Failed to fetch section passes:',
          sectionPassesResult.reason
        )
      }

      if (tokensResult.status === 'fulfilled') {
        this.tokens = tokensResult.value
      } else {
        console.warn('Failed to fetch tokens:', tokensResult.reason)
      }

      if (purchasesResult.status === 'fulfilled') {
        this.purchases = purchasesResult.value
      } else {
        console.warn('Failed to fetch purchases:', purchasesResult.reason)
      }

      this.loadingState = hasCriticalError ? 'error' : 'loaded'
    })
  }

  @action
  async fetchSlideDeckName(slideDeckId: string): Promise<string> {
    if (this.slideDeckNames.has(slideDeckId)) {
      return this.slideDeckNames.get(slideDeckId)!
    }

    try {
      const slideDeckRef = doc(
        collection(this.repository.firestore, 'slide_deck'),
        slideDeckId
      )
      const slideDeckSnap = await getDocWithError(
        slideDeckRef,
        'FetchSlideDeckNameError'
      )

      if (slideDeckSnap.exists()) {
        const name =
          (slideDeckSnap.data() as { name?: string }).name || 'Unknown'
        runInAction(() => {
          this.slideDeckNames.set(slideDeckId, name)
        })
        return name
      }
    } catch (err) {
      console.warn('Error fetching slide deck name:', err)
    }

    return 'Unknown'
  }

  @computed
  get isLoading(): boolean {
    return this.loadingState === 'loading'
  }

  @computed
  get isLoaded(): boolean {
    return this.loadingState === 'loaded'
  }

  @computed
  get hasError(): boolean {
    return this.loadingState === 'error'
  }

  @computed
  get userFullName(): string {
    if (!this.user) return ''
    return this.user.fullName
  }

  @computed
  get userInitials(): string {
    if (!this.user) return ''
    return this.user.initials
  }
}
